import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { notificationsApi, type AdminNotification } from '../api/notifications.api.js';

/**
 * What the platform needs somebody to look at, wherever they are in the console.
 *
 * The inbound review queue made the need obvious: money arrives, is correctly
 * held, and then waits for as long as it takes somebody to open the right
 * screen. A count on the dashboard only reaches a person already on the
 * dashboard — this reaches them on every page.
 *
 * Opening a notification marks it read and goes to the work. Read state is not
 * the point; the count is, and a count that never falls stops being read at all.
 */

const POLL_MS = 60_000;

function ago(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${String(minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}h ago`;
  return `${String(Math.floor(hours / 24))}d ago`;
}

export function NotificationBell(): JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const feed = useQuery({
    queryKey: ['admin-notifications'],
    queryFn: () => notificationsApi.list(30),
    refetchInterval: POLL_MS,
  });

  const markRead = useMutation({
    mutationFn: (ids?: readonly string[]) => notificationsApi.markRead(ids),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
    },
  });

  const items = feed.data?.items ?? [];
  const unread = feed.data?.unread ?? 0;

  const openItem = (item: AdminNotification): void => {
    setAnchor(null);
    if (item.readAt === null) markRead.mutate([item.id]);
    if (item.link !== null && item.link !== '') navigate(item.link);
  };

  return (
    <>
      <IconButton
        color="inherit"
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={unread === 0 ? 'Notifications' : `${String(unread)} unread notifications`}
      >
        <Badge badgeContent={unread} color="error" max={99}>
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 420, maxHeight: 520 } } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            Needs attention
          </Typography>
          {unread > 0 && (
            <Button size="small" onClick={() => markRead.mutate(undefined)} disabled={markRead.isPending}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {feed.isLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Nothing needs attention.
            </Typography>
          </Box>
        ) : (
          items.map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => openItem(item)}
              sx={{
                display: 'block',
                whiteSpace: 'normal',
                py: 1.25,
                borderLeft: 3,
                borderColor:
                  item.readAt !== null
                    ? 'transparent'
                    : item.severity === 'ACTION_REQUIRED'
                      ? 'error.main'
                      : 'info.main',
              }}
            >
              <Stack direction="row" alignItems="baseline" gap={1}>
                <Typography
                  variant="body2"
                  sx={{ flexGrow: 1, fontWeight: item.readAt === null ? 600 : 400 }}
                >
                  {item.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ago(item.createdAt)}
                </Typography>
              </Stack>
              {item.body !== null && (
                <Typography variant="caption" color="text.secondary" component="div">
                  {item.body}
                </Typography>
              )}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
